import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumGate } from '../hooks/usePremiumGate';
import { logPaywall } from '../lib/paywallAnalytics';
import { Button } from './ui/button';
import { ArrowLeft, LayoutDashboard, LogOut, Crown, Sparkles, Languages } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';

// Premium-only standalone tools (per the paid plan feature list). Atlas,
// word/basic search, reading plans, VOTD, community & videos stay FREE.
import PremiumToolLock, { isPremiumTool } from './PremiumToolLock';

// Lazy load all tool components
const BibleReader = lazy(() => import('./BibleReader'));
const AIInsights = lazy(() => import('./AIInsights'));
const AIInsightsStandalone = lazy(() => import('./AIInsightsStandalone'));
const ParallelView = lazy(() => import('./ParallelView'));
const ConcordanceTool = lazy(() => import('./ConcordanceTool'));
const CommentaryTool = lazy(() => import('./CommentaryTool'));
const NotesPanel = lazy(() => import('./NotesPanel'));
const WordStudyPanel = lazy(() => import('./WordStudyPanel'));
const InterlinearView = lazy(() => import('./InterlinearView'));
const CrossReferencesPanel = lazy(() => import('./CrossReferencesPanel'));
const VerseComparisonTool = lazy(() => import('./VerseComparisonTool'));
const BookmarksPanel = lazy(() => import('./BookmarksPanel'));
const BibleDictionary = lazy(() => import('./BibleDictionary'));
const BibleMaps = lazy(() => import('./BibleMaps'));
const DevotionalTool = lazy(() => import('./DevotionalTool'));
const GospelHarmonyTool = lazy(() => import('./GospelHarmonyTool'));
const CharacterEncyclopedia = lazy(() => import('./CharacterEncyclopedia'));
const AICompanion = lazy(() => import('./AICompanion'));
const LumeraCompanion = lazy(() => import('./LumeraCompanion'));
const UserProfile = lazy(() => import('./UserProfile'));
const VerseOfTheDay = lazy(() => import('./VerseOfTheDay'));
const ReadingPlansView = lazy(() => import('./ReadingPlansView'));
const HighlightsViewer = lazy(() => import('./HighlightsViewer'));
const VerseImageGenerator = lazy(() => import('./VerseImageGenerator'));
const NotificationSettings = lazy(() => import('./NotificationSettings'));
const VideosHub = lazy(() => import('./VideosHub'));
const ChronologyExplorer = lazy(() => import('./ChronologyExplorer'));
const BibleAtlasPane = lazy(() => import('./BibleAtlasPane'));
const TopicalBiblePane = lazy(() => import('./TopicalBiblePane'));
const HistoricReadingPlansPane = lazy(() => import('./HistoricReadingPlansPane'));
const ApocryphaPane = lazy(() => import('./ApocryphaPane'));
const OriginalLanguagesPane = lazy(() => import('./OriginalLanguagesPane'));
const ISBEEncyclopediaPane = lazy(() => import('./ISBEEncyclopediaPane'));

// Loading fallback
const LoadingFallback = () => (
  <Card className="h-full">
    <CardContent className="flex items-center justify-center h-full min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading...</p>
      </div>
    </CardContent>
  </Card>
);

/**
 * Standalone Tool Page Wrapper
 * Renders any dashboard tool as a full standalone page with navigation
 */
const StandaloneToolPage = ({ toolId }) => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { logout } = useAuth();
  const { isPremium, user } = usePremiumGate();
  const isLockedPremium = isPremiumTool(toolId) && !isPremium;

  
  // Standalone tool pages have no Bible reader mounted, so a tool that fires
  // `lumera:verse-select` (chronology maps, atlas, topical, cross-refs, etc.)
  // would otherwise do nothing on click. Stash the ref and hand off to the
  // dashboard, which opens the reader on that verse. 'original-languages'
  // consumes the event in-page, so it's excluded.
  useEffect(() => {
    if (toolId === 'original-languages') return undefined;
    const handler = (e) => {
      const ref = e?.detail?.reference;
      if (!ref) return;
      try { sessionStorage.setItem('lumera_pending_verse', ref); } catch (_) { /* noop */ }
      navigate('/dashboard');
    };
    window.addEventListener('lumera:verse-select', handler);
    return () => window.removeEventListener('lumera:verse-select', handler);
  }, [toolId, navigate]);

  // Default verse for tools that need it
  const [selectedVerse] = useState({
    book: 'John',
    chapter: 3,
    verse: 16,
    text: 'For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.',
    translation: 'KJV',
    reference: 'John 3:16'
  });

  // Get tool title
  const getToolTitle = () => t(`tools.${toolId}`);

  // Render the appropriate tool component
  const renderTool = () => {
    // Premium gate: signed-in free users (and guests) see an upgrade wall for
    // paid-only tools instead of the tool itself.
    if (isLockedPremium) {
      return <div className="max-w-2xl mx-auto mt-6"><PremiumToolLock toolId={toolId} title={getToolTitle()} /></div>;
    }
    switch (toolId) {
      case 'profile':
        return <UserProfile />;
      case 'maps':
        return <BibleMaps />;
      case 'wordstudy':
        return <WordStudyPanel />;
      case 'concordance':
        return <ConcordanceTool currentVerse={selectedVerse} />;
      case 'commentary':
        return <CommentaryTool currentVerse={selectedVerse} />;
      case 'notes':
        return <NotesPanel currentVerse={selectedVerse} />;
      case 'dictionary':
        return <BibleDictionary />;
      case 'characters':
        return <CharacterEncyclopedia />;
      case 'devotional':
        return <DevotionalTool />;
      case 'bookmarks':
        return <BookmarksPanel />;
      case 'crossrefs':
        return <CrossReferencesPanel currentVerse={selectedVerse} />;
      case 'reading-plans':
        return <ReadingPlansView />;
      case 'highlights':
        return <HighlightsViewer />;
      case 'verse-image':
        return <VerseImageGenerator initialVerse={selectedVerse} />;
      case 'parallel':
        return <ParallelView reference={`${selectedVerse.book} ${selectedVerse.chapter}:${selectedVerse.verse}`} />;
      case 'comparison':
        return <VerseComparisonTool initialReference={selectedVerse} />;
      case 'interlinear':
        return <InterlinearView reference={selectedVerse.reference} />;
      case 'votd':
        return <VerseOfTheDay />;
      case 'companion':
        return <LumeraCompanion />;
      case 'videos':
        return <VideosHub />;
      case 'notifications':
        return <NotificationSettings />;
      case 'chronology':
        return <ChronologyExplorer />;
      case 'atlas':
        return <BibleAtlasPane />;
      case 'harmony':
        return <GospelHarmonyTool />;
      case 'topical':
        return <TopicalBiblePane />;
      case 'historic-plans':
        return <HistoricReadingPlansPane />;
      case 'apocrypha':
        return <ApocryphaPane />;
      case 'original-languages':
        return <OriginalLanguagesPane />;
      case 'isbe':
        return <ISBEEncyclopediaPane />;
      case 'ai':
        return <AIInsightsStandalone initialReference={selectedVerse} />;
      default:
        return (
          <Card>
            <CardContent className="py-12 text-center text-slate-700 dark:text-slate-400">
              {t('tools.notFound')}
            </CardContent>
          </Card>
        );
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="gradient-primary border-b border-white/20 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Left: Back + Title */}
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20 border border-white/30"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-2xl">✝️</span>
              <h1 className="text-lg sm:text-xl font-bold text-white">
                {getToolTitle()}
              </h1>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
              title={t('header.switchLang')}
              data-testid="standalone-language-toggle"
            >
              <Languages className="w-4 h-4 mr-1" />
              <span className="text-xs font-semibold">
                {language === 'es' ? 'ES' : 'EN'}
              </span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 border border-white/30 px-3"
              title={t('tools.goToDashboard')}
            >
              <LayoutDashboard className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline text-xs">
                {t('tools.dashboard')}
              </span>
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/20 border border-white/30 px-3"
              title={t('tools.logout')}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto p-4 sm:p-6 max-w-7xl mx-auto w-full">
        <Suspense fallback={<LoadingFallback />}>
          {renderTool()}
        </Suspense>
      </main>
    </div>
  );
};

export default StandaloneToolPage;
